import React, { useState, useEffect } from "react";
import { Dropdown, Menu } from "antd";
import moment from "moment";
import { useSelector } from "react-redux";

import Loader from "../../../../core/common/loader";
import TodoModal from "../../../../core/modals/todoModal";
import TransferTaskModal from "../../../../core/modals/transferTaskModal";
import TaskHistoryModal from "../../../../core/modals/taskHistoryModal";
import DeleteModal from "../../../../core/modals/deleteModal";

import { useTodoData } from "../hooks/useTodoData";
import TodoToolbar from "./TodoToolbar";
import TodoFilterPanel from "./TodoFilterPanel";
import TodoStatsBar from "./TodoStatsBar";
import {
  TAG_OPTIONS,
  STATUS_OPTIONS,
  RECURRING_OPTIONS,
  PRIORITY_OPTIONS,
  STAGE_OPTIONS,
  GRID_INITIAL_FILTERS,
} from "../constants";
import {
  formatTitle,
  capitalizeFirstLetter,
  getTagClass,
  getStageClass,
  getEmployeeImageUrl,
} from "../helpers";
import type { Todo, DateRangeFilter, ViewType, Option } from "../types";

interface TodoGridViewProps {
  view: ViewType;
}

const TodoGridView: React.FC<TodoGridViewProps> = ({ view }) => {
  const user = useSelector((state: any) => state.user.user);

  const {
    signature,
    allData,
    isLoading,
    selectedCompanyId,
    selectedCompanyType,
    selectedCompany,
    handleCompanySelect,
    refreshTodos,
    updateReadStatus,
    handleStageUpdate,
    handleDeleteTodo,
    handleTaskTransferred,
  } = useTodoData();

  const [filteredData, setFilteredData] = useState<{ todos: Todo[] }>({
    todos: [],
  });

  // Applied filter state
  const [selectedPriority, setSelectedPriority] = useState<string>("all");
  const [selectedTag, setSelectedTag] = useState<string>("All Tags");
  const [selectedStatus, setSelectedStatus] = useState<string>("All");
  const [selectedAssignee, setSelectedAssignee] =
    useState<string>("All Assignees");
  const [selectedFollower, setSelectedFollower] =
    useState<string>("All Follower");
  const [selectedRecurring, setSelectedRecurring] = useState<string>("All");
  const [sortBy, setSortBy] = useState<string>("Priority");
  const [selectedReporter, setSelectedReporter] =
    useState<string>("All Reporters");
  const [dueDateFilter, setDueDateFilter] = useState<DateRangeFilter | null>(
    null,
  );
  const [createDateFilter, setcreateDateFilter] =
    useState<DateRangeFilter | null>(null);

  // Temp filters used by the panel before "Apply".
  const [tempFilters, setTempFilters] = useState({ ...GRID_INITIAL_FILTERS });

  // Modal + UI state
  const [viewModalTodo, setViewModalTodo] = useState<Todo | null>(null);
  const [editModalTodo, setEditModalTodo] = useState<Todo | null>(null);
  const [deleteTodoId, setDeleteTodoId] = useState<string | null>(null);
  const [transferModalTodo, setTransferModalTodo] = useState<Todo | null>(null);
  const [logsModalTodo, setLogsModalTodo] = useState<Todo | null>(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);
  const [accordionOpen, setAccordionOpen] = useState({
    high: true,
    medium: true,
    low: true,
    completed: true,
  });

  const [assigneeOptions, setAssigneeOptions] = useState<Option[]>([
    { value: "All Assignees", label: "All Assignees" },
  ]);
  const [reporterOptions, setReporterOptions] = useState<Option[]>([
    { value: "All Reporters", label: "All Reporters" },
  ]);

  const activeFiltersCount = Object.entries(tempFilters).reduce(
    (count, [key, value]) => {
      if (key === "selectedCompanyId" && value) return count + 1;
      if (key === "selectedPriority" && value !== "all") return count + 1;
      if (key === "selectedTag" && value !== "All Tags") return count + 1;
      if (key === "selectedAssignee" && value !== "All Assignees")
        return count + 1;
      if (key === "selectedFollower" && value !== "All Follower")
        return count + 1;
      if (key === "sortBy" && value !== "Priority") return count + 1;
      if (key === "dueDateFilter" && value) return count + 1;
      if (key === "createDateFilter" && value) return count + 1;
      if (key === "selectedRecurring" && value !== "All") return count + 1;
      return count;
    },
    -1,
  );

  // Build assignee options when the company changes.
  useEffect(() => {
    if (selectedCompanyId !== null && selectedCompanyType !== null) {
      const companyUsers = allData.users.filter(
        (u) =>
          u.CompanyId == selectedCompanyId &&
          u.CompanyType == selectedCompanyType,
      );
      const options = companyUsers.map((u) => ({
        value: (u.EmpId || u.id) as string,
        label: (u.Name || u.name) as string,
      }));
      setAssigneeOptions(options);
      setTempFilters((prev) => ({ ...prev, selectedAssignee: "All Assignees" }));
      setSelectedAssignee("All Assignees");
    }
  }, [selectedCompanyType, selectedCompanyId, allData.users]);

  // Sync temp filters when the panel opens.
  useEffect(() => {
    if (isFilterPanelOpen) {
      setTempFilters({
        selectedCompanyId,
        selectedPriority,
        selectedTag,
        selectedStatus,
        selectedRecurring,
        selectedAssignee,
        selectedFollower,
        selectedReporter,
        sortBy,
        dueDateFilter,
        createDateFilter,
      });
    }
  }, [
    isFilterPanelOpen,
    selectedCompanyId,
    selectedPriority,
    selectedTag,
    selectedStatus,
    selectedAssignee,
    selectedFollower,
    selectedRecurring,
    selectedReporter,
    sortBy,
    dueDateFilter,
    createDateFilter,
  ]);

  // Build reporter options from the current company's todos.
  useEffect(() => {
    if (allData.todos.length > 0 && selectedCompanyId && selectedCompanyType) {
      const companyTodos = allData.todos.filter(
        (todo) =>
          todo.company_id == selectedCompanyId &&
          todo.company_type == selectedCompanyType,
      );
      const uniqueReporters = companyTodos.reduce(
        (acc, todo) => {
          if (
            todo.reporter?.uid &&
            !acc.some((r) => r.value === todo.reporter?.uid.toString())
          ) {
            if (user?.userDetails.role_char.toLowerCase() === "admin") {
              acc.push({
                value: todo.reporter.uid.toString(),
                label: todo.reporter.name || "",
              });
            } else if (
              user?.userDetails.uid.toString() == todo.reporter?.uid.toString()
            ) {
              acc.push({
                value: todo.reporter.uid.toString(),
                label: todo.reporter.name || "",
              });
            }
          }
          return acc;
        },
        [] as Option[],
      );
      setReporterOptions(uniqueReporters);
    }
  }, [allData.todos, selectedCompanyId, selectedCompanyType, user]);

  // Set initial company selection once companies load.
  useEffect(() => {
    if (allData.companies.length > 0 && !selectedCompanyId) {
      const firstCompany = allData.companies[0];
      handleCompanySelect(
        {
          value: `${firstCompany.id}-${firstCompany.type}`,
          label: `${firstCompany.name} - ${firstCompany.type}`,
        },
        (companyId) =>
          setTempFilters((prev) => ({ ...prev, selectedCompanyId: companyId })),
      );
    }
  }, [allData.companies, selectedCompanyId, handleCompanySelect]);

  // Re-filter on data / filter changes.
  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCompanyId,
    selectedCompanyType,
    selectedPriority,
    selectedTag,
    selectedStatus,
    selectedAssignee,
    selectedFollower,
    selectedRecurring,
    selectedReporter,
    dueDateFilter,
    createDateFilter,
    sortBy,
    allData,
  ]);

  // ---- filtering ---------------------------------------------------------
  const filterData = () => {
    let filteredTodos: Todo[] = allData.todos;

    if (selectedCompanyId && selectedCompanyType) {
      filteredTodos = filteredTodos.filter(
        (todo) =>
          todo.company_id == selectedCompanyId &&
          todo.company_type == selectedCompanyType,
      );
    }

    if (selectedPriority === "completed") {
      filteredTodos = filteredTodos.filter((todo) => todo.stage === 3);
    } else if (selectedPriority !== "all") {
      filteredTodos = filteredTodos.filter(
        (todo) =>
          todo.priority === parseInt(selectedPriority) && todo.stage !== 3,
      );
    } else if (selectedStatus === "3") {
      filteredTodos = filteredTodos.filter((todo) => todo.stage === 3);
    } else {
      filteredTodos = filteredTodos.filter((todo) => todo.stage !== 3);
    }

    if (selectedStatus !== "All") {
      if (selectedStatus === "overdue") {
        filteredTodos = filteredTodos.filter((todo) => {
          const dueDate = todo.due_date ? new Date(todo.due_date) : null;
          const todayDate = new Date();
          todayDate.setHours(0, 0, 0, 0);
          if (dueDate) dueDate.setHours(0, 0, 0, 0);
          return dueDate && todayDate > dueDate && todo.stage !== 3;
        });
      } else if (selectedStatus !== "3") {
        filteredTodos = filteredTodos.filter(
          (todo) => todo.stage === parseInt(selectedStatus),
        );
      }
    }

    if (selectedRecurring !== "All") {
      filteredTodos = filteredTodos.filter((todo) =>
        selectedRecurring === "Recurring"
          ? todo.recurring_date !== null
          : todo.recurring_date === null,
      );
    }

    if (selectedReporter !== "All Reporters") {
      filteredTodos = filteredTodos.filter(
        (todo) => todo.reporter?.uid.toString() === selectedReporter,
      );
    }

    if (selectedTag !== "All Tags") {
      filteredTodos = filteredTodos.filter((todo) =>
        todo.tags.includes(selectedTag),
      );
    }

    if (selectedAssignee !== "All Assignees") {
      filteredTodos = filteredTodos.filter((todo) =>
        todo.assigned_members.some((member) => member.id === selectedAssignee),
      );
    }

    if (selectedFollower !== "All Follower") {
      filteredTodos = filteredTodos.filter((todo) =>
        todo.team_leads.some((member) => member.id === selectedFollower),
      );
    }

    if (dueDateFilter && dueDateFilter.start && dueDateFilter.end) {
      const startDate = moment(dueDateFilter.start).startOf("day");
      const endDate = moment(dueDateFilter.end).endOf("day");
      filteredTodos = filteredTodos.filter((todo) => {
        if (!todo.due_date) return false;
        const todoDate = moment(todo.due_date);
        return todoDate >= startDate && todoDate <= endDate;
      });
    }

    if (createDateFilter && createDateFilter.start && createDateFilter.end) {
      const startDate = moment(createDateFilter.start).startOf("day");
      const endDate = moment(createDateFilter.end).endOf("day");
      filteredTodos = filteredTodos.filter((todo) => {
        if (!todo.createdAt) return false;
        const todoDate = moment(todo.createdAt);
        return todoDate >= startDate && todoDate <= endDate;
      });
    }

    filteredTodos = [...filteredTodos].sort((a, b) => {
      switch (sortBy) {
        case "Created Date":
          return (
            new Date(b.createdAt || "").getTime() -
            new Date(a.createdAt || "").getTime()
          );
        case "Priority":
          return a.priority - b.priority;
        case "Due Date":
          return (
            new Date(a.due_date || "").getTime() -
            new Date(b.due_date || "").getTime()
          );
        case "Recurring":
          return (b.recurring_date ? 1 : 0) - (a.recurring_date ? 1 : 0);
        case "Non-Recurring":
          return (a.recurring_date ? 1 : 0) - (b.recurring_date ? 1 : 0);
        default:
          return 0;
      }
    });

    setFilteredData({ todos: filteredTodos || [] });
  };

  // ---- handlers ----------------------------------------------------------
  const onCompanySelect = (option: any) =>
    handleCompanySelect(option, (companyId) =>
      setTempFilters((prev) => ({ ...prev, selectedCompanyId: companyId })),
    );

  const handlePriorityChange = (priority: string) => {
    setSelectedPriority(priority);
    setTempFilters((prev) => ({ ...prev, selectedPriority: priority }));
  };

  const handleTagFilterChange = (tag: string) =>
    setTempFilters((prev) => ({ ...prev, selectedTag: tag }));
  const handleAssigneeFilterChange = (assignee: string) =>
    setTempFilters((prev) => ({ ...prev, selectedAssignee: assignee }));
  const handleFollowerFilterChange = (assignee: string) =>
    setTempFilters((prev) => ({
      ...prev,
      selectedFollower: assignee === "Assignee" ? "All" : assignee,
    }));
  const handleStatusFilterChange = (status: string) =>
    setTempFilters((prev) => ({ ...prev, selectedStatus: status }));
  const handleRecurringFilterChange = (recurring: string) =>
    setTempFilters((prev) => ({ ...prev, selectedRecurring: recurring }));
  const handleReporterFilterChange = (reporter: string) =>
    setTempFilters((prev) => ({ ...prev, selectedReporter: reporter }));

  const applyFilters = () => {
    setSelectedPriority(tempFilters.selectedPriority);
    setSelectedTag(tempFilters.selectedTag);
    setSelectedAssignee(tempFilters.selectedAssignee);
    setSelectedFollower(tempFilters.selectedFollower);
    setSelectedReporter(tempFilters.selectedReporter);
    setSortBy(tempFilters.sortBy);
    setDueDateFilter(tempFilters.dueDateFilter);
    setcreateDateFilter(tempFilters.createDateFilter);
    setSelectedStatus(tempFilters.selectedStatus);
    setSelectedRecurring(tempFilters.selectedRecurring);
    setIsFilterPanelOpen(false);
  };

  const resetFilters = () => {
    setSelectedPriority(GRID_INITIAL_FILTERS.selectedPriority);
    setSelectedTag(GRID_INITIAL_FILTERS.selectedTag);
    setSelectedAssignee(GRID_INITIAL_FILTERS.selectedAssignee);
    setSelectedFollower(GRID_INITIAL_FILTERS.selectedFollower);
    setSelectedReporter(GRID_INITIAL_FILTERS.selectedReporter);
    setSortBy(GRID_INITIAL_FILTERS.sortBy);
    setSelectedStatus(GRID_INITIAL_FILTERS.selectedStatus);
    setDueDateFilter(GRID_INITIAL_FILTERS.dueDateFilter);
    setcreateDateFilter(GRID_INITIAL_FILTERS.createDateFilter);
    setSelectedRecurring(GRID_INITIAL_FILTERS.selectedRecurring);
    setTempFilters({ ...GRID_INITIAL_FILTERS });
    filterData();
  };

  const handleViewTodo = async (todo: Todo) => {
    setViewModalTodo(todo);
    if (!todo.isRead) {
      try {
        await updateReadStatus(todo.id || "");
      } catch {
        /* non-fatal */
      }
    }
  };

  const handleEditTodo = (todo: Todo) => setEditModalTodo(todo);
  const handleAddTodo = () => setEditModalTodo(null);

  const handleModalSave = async () => {
    await refreshTodos();
    setEditModalTodo(null);
  };

  const toggleAccordion = (
    priority: "high" | "medium" | "low" | "completed",
  ) => {
    setAccordionOpen((prev) => ({ ...prev, [priority]: !prev[priority] }));
  };

  const toggleAllAccordions = () => {
    setAccordionOpen((prev) => {
      const allOpen = prev.high && prev.medium && prev.low && prev.completed;
      return {
        high: !allOpen,
        medium: !allOpen,
        low: !allOpen,
        completed: !allOpen,
      };
    });
  };

  const toggleTodo = async (todoId: string) => {
    const todo = allData.todos.find((t) => t.id === todoId);
    if (!todo) return;
    const newStage = todo.stage === 3 ? 0 : 3;
    await handleStageUpdate(todoId, newStage.toString());
  };

  // ---- stats -------------------------------------------------------------
  const pendingTodos = filteredData.todos.filter((t) => t.stage === 0).length;
  const inProgressTodos = filteredData.todos.filter(
    (t) => t.stage === 1,
  ).length;
  const onHoldTodos = filteredData.todos.filter((t) => t.stage === 2).length;
  const completedTodos = filteredData.todos.filter(
    (t) => t.stage === 3,
  ).length;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdueTodos = filteredData.todos.filter((todo) => {
    const dueDate = new Date(todo.due_date);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today && todo.stage !== 3;
  }).length;

  const completedTodosSection = filteredData.todos.filter(
    (todo) => todo.stage === 3,
  );

  // ---- search ------------------------------------------------------------
  const handleSearch = (search: string) => {
    const q = search.toLowerCase();
    const filteredTodos = allData.todos.filter((todo) => {
      const idMatch = todo.id?.toString().includes(q);
      const titleMatch = todo.title?.toLowerCase().includes(q);
      const descriptionMatch = todo.description?.toLowerCase().includes(q);
      const priorityLabel =
        PRIORITY_OPTIONS.find((p) => parseInt(p.value) === todo.priority)
          ?.label?.toLowerCase() || "";
      const priorityMatch = priorityLabel.includes(q);
      const reporterMatch = todo.reporter?.name?.toLowerCase().includes(q);
      const followersMatch = todo.team_leads?.some((m) =>
        m.name?.toLowerCase().includes(q),
      );
      const membersMatch = todo.assigned_members?.some((m) =>
        m.name?.toLowerCase().includes(q),
      );
      return (
        idMatch ||
        titleMatch ||
        descriptionMatch ||
        priorityMatch ||
        reporterMatch ||
        followersMatch ||
        membersMatch
      );
    });
    setFilteredData((prev) => ({ ...prev, todos: filteredTodos }));
  };

  // ---- rendering ---------------------------------------------------------
  const renderTodoItem = (todo: Todo) => {
    const stageLabel = STAGE_OPTIONS.find(
      (opt) => parseInt(opt.value) === todo.stage,
    )?.label;
    const tag = todo.tags[0] || "N/A";
    const isCompleted = todo.stage === 3;

    const menu = (
      <Menu>
        <Menu.Item key="view">
          <button
            className="dropdown-item"
            onClick={() => handleViewTodo(todo)}
            data-bs-toggle="modal"
            data-bs-target="#view_todo"
          >
            <i className="ti ti-eye me-2" /> View
          </button>
        </Menu.Item>
        <Menu.Item key="edit">
          <button
            className="dropdown-item"
            onClick={() => handleEditTodo(todo)}
            data-bs-toggle="modal"
            data-bs-target="#add_todo"
          >
            <i className="ti ti-edit me-2" /> Edit
          </button>
        </Menu.Item>
        {(user?.userDetails?.role_char === "Admin" ||
          user?.userDetails?.uid === todo.reporter?.uid) && (
          <Menu.Item key="transfer">
            <button
              className="dropdown-item"
              onClick={() => setTransferModalTodo(todo)}
            >
              <i className="ti ti-transfer me-2" /> Transfer
            </button>
          </Menu.Item>
        )}
        <Menu.Item key="history">
          <button
            className="dropdown-item"
            onClick={() => setLogsModalTodo(todo)}
          >
            <i className="ti ti-history me-2" /> History
          </button>
        </Menu.Item>
        {(user?.userDetails?.role_char === "Admin" ||
          user?.userDetails?.uid === todo.reporter?.uid) && (
          <Menu.Item key="delete">
            <button
              className="dropdown-item text-danger"
              onClick={() => setDeleteTodoId(todo.id || "")}
              data-bs-toggle="modal"
              data-bs-target="#delete_modal"
            >
              <i className="ti ti-trash me-2" /> Delete
            </button>
          </Menu.Item>
        )}
      </Menu>
    );

    return (
      <div
        key={todo.id}
        className="list-group-item list-item-hover shadow-sm rounded mb-2 p-3"
      >
        <div className="row align-items-center row-gap-3">
          <div className="col-lg-6 col-md-7">
            <div
              className={`todo-inbox-check d-flex align-items-center flex-wrap row-gap-3 ${
                isCompleted ? "todo-strike-content" : ""
              }`}
            >
              <span className="me-2 d-flex align-items-center">
                <i className="ti ti-grid-dots text-dark" />
              </span>
              <div className="form-check form-check-md me-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isCompleted}
                  onChange={() => toggleTodo(todo.id || "")}
                />
              </div>
              <span className="me-2 rating-select d-flex align-items-center">
                {todo.isRead ? (
                  <i className="ti ti-checks text-success" />
                ) : (
                  <i className="ti ti-check" />
                )}
              </span>
              <div className="strike-info">
                <h4
                  className="fs-14"
                  title={capitalizeFirstLetter(todo.title)}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: "200px",
                    maxWidth: "200px",
                  }}
                >
                  {formatTitle(todo.title)}
                </h4>
              </div>
              <span className="badge bg-transparent-dark text-dark rounded-pill ms-2">
                <i className="ti ti-calendar me-1" />
                {todo.createdAt
                  ? moment(todo.createdAt).format("DD MMM YYYY")
                  : "N/A"}
              </span>
              <span className="badge bg-danger-subtle text-danger rounded-pill ms-2">
                <i className="ti ti-calendar me-1" />
                {todo.due_date
                  ? moment(todo.due_date).format("DD MMM YYYY")
                  : "N/A"}
                <br />
                {todo.due_date &&
                  moment(todo.due_date).isBefore(moment(), "day") && (
                    <small className="text-danger">
                      {moment().diff(moment(todo.due_date), "days")} Day
                      {moment().diff(moment(todo.due_date), "days") > 1
                        ? "s"
                        : ""}{" "}
                      Overdue
                    </small>
                  )}
              </span>
            </div>
          </div>
          <div className="col-lg-6 col-md-5">
            <div className="d-flex align-items-center justify-content-md-end flex-wrap gap-3">
              <span
                className={`badge rounded-pill ${getTagClass(
                  tag,
                )} px-3 py-2 text-uppercase fw-semibold`}
                style={{ width: 100 }}
              >
                {tag}
              </span>
              <span
                className={`badge rounded-pill ${getStageClass(
                  stageLabel || "",
                )} d-flex align-items-center px-3 py-2`}
              >
                <i className="fas fa-circle fs-6 me-2" />
                {stageLabel || "N/A"}
              </span>
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center position-relative">
                  {todo.assigned_members.slice(0, 3).map((member, index) => (
                    <div
                      key={index}
                      className="position-relative rounded-circle border border-white overflow-hidden"
                      style={{
                        width: 36,
                        height: 36,
                        zIndex: 10 - index,
                        marginLeft: index !== 0 ? "-12px" : "0",
                        backgroundColor: "#ccc",
                        boxShadow: "0 0 0 2px white",
                      }}
                      title={member?.name}
                    >
                      {member &&
                      getEmployeeImageUrl(allData.users, member.id) ? (
                        <img
                          src={getEmployeeImageUrl(allData.users, member.id)}
                          alt={member.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center text-white bg-primary"
                          style={{
                            width: "100%",
                            height: "100%",
                            fontSize: 14,
                            fontWeight: "bold",
                          }}
                        >
                          {member?.name?.charAt(0).toUpperCase() || "?"}
                        </div>
                      )}
                    </div>
                  ))}
                  {todo.assigned_members.length > 3 && (
                    <span
                      className="rounded-circle bg-light border border-white text-muted d-flex align-items-center justify-content-center"
                      style={{
                        width: 36,
                        height: 36,
                        marginLeft: "-12px",
                        fontSize: 14,
                        fontWeight: 600,
                        zIndex: 0,
                        boxShadow: "0 0 0 2px white",
                      }}
                      title={`+${todo.assigned_members.length - 3} more`}
                    >
                      +{todo.assigned_members.length - 3}
                    </span>
                  )}
                </div>
                <Dropdown
                  overlay={menu}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    className="btn btn-sm btn-light"
                    type="button"
                    onClick={(e) => e.preventDefault()}
                  >
                    <i className="ti ti-dots-vertical" />
                  </button>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAccordionSection = (
    priority: "high" | "medium" | "low" | "completed",
    todos: Todo[],
    priorityLabel: string,
    iconClass: string,
  ) => {
    const isOpen = accordionOpen[priority];
    const accordionId = `collapse${
      priority.charAt(0).toUpperCase() + priority.slice(1)
    }`;
    const headingId = `heading${
      priority.charAt(0).toUpperCase() + priority.slice(1)
    }`;

    const styles = {
      accordionItem: {
        border: "5px solid #dee2e6",
        borderRadius: "8px",
        marginBottom: "1rem",
        overflow: "hidden",
      },
      accordionButton: {
        backgroundColor: "#f8f9fa",
        padding: "0.75rem 1rem",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        transition: "background-color 0.3s ease",
      },
      accordionButtonHover: { backgroundColor: "#e9ecef" },
      accordionBody: {
        backgroundColor: "#ffffff",
        padding: "1rem",
        borderTop: "1px solid #dee2e6",
      },
      iconChevron: { marginRight: "1rem" },
      badge: {
        backgroundColor: "#f1f3f5",
        color: "#212529",
        borderRadius: "999px",
        padding: "0.35rem 0.75rem",
        fontSize: "0.85rem",
      },
    };

    const getIcon = () =>
      priority === "completed" ? (
        <i className={`ti ti-check ${iconClass} me-2`} />
      ) : (
        <i className={`ti ti-square-rounded ${iconClass} me-2`} />
      );

    return (
      <div style={styles.accordionItem} key={priority}>
        <div className="accordion-header" id={headingId}>
          <div
            className={`accordion-button ${isOpen ? "" : "collapsed"}`}
            aria-controls={accordionId}
            aria-expanded={isOpen}
            onClick={() => toggleAccordion(priority)}
            style={styles.accordionButton}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                styles.accordionButtonHover.backgroundColor)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                styles.accordionButton.backgroundColor)
            }
          >
            <div className="d-flex align-items-center">
              <i
                className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
                style={styles.iconChevron}
              />
              {getIcon()}
              <h5 className="fw-semibold mb-0">{priorityLabel}</h5>
            </div>
            <span style={styles.badge}>{todos.length}</span>
          </div>
        </div>
        <div
          id={accordionId}
          className={`accordion-collapse collapse ${isOpen ? "show" : ""}`}
          aria-labelledby={headingId}
        >
          <div style={styles.accordionBody}>
            <div className="list-group list-group-flush border-bottom pb-2">
              {todos.length > 0 ? (
                todos.map((todo) => renderTodoItem(todo))
              ) : (
                <div className="text-center py-3 text-muted">
                  {priority === "completed"
                    ? "No completed todos available"
                    : "No todos available"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ---- options for the filter panel -------------------------------------
  const reporterImageOptions = reporterOptions.map((u: any) => ({
    label: u.label,
    value: u.value,
    imageUrl: getEmployeeImageUrl(allData.users, u.value),
  }));
  const assigneeImageOptions = assigneeOptions.map((u: any) => ({
    label: u.label,
    value: u.value,
    imageUrl: getEmployeeImageUrl(allData.users, u.value),
  }));

  return (
    <>
      {isLoading && <Loader />}
      <div className="page-wrapper">
        <div className="content">
          <TodoToolbar
            view={view}
            companies={allData.companies}
            selectedCompanyId={selectedCompanyId}
            selectedCompanyType={selectedCompanyType}
            onCompanySelect={onCompanySelect}
            onAddTodo={handleAddTodo}
            onOpenFilters={() => setIsFilterPanelOpen(true)}
            activeFiltersCount={activeFiltersCount}
          />

          <div className="card">
            <div className="card-body">
              <div className="row gy-3 mb-3">
                <div className="col-sm-4">
                  <div className="d-flex align-items-center">
                    <h4>Total Task</h4>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="d-flex align-items-center justify-content-end">
                    <TodoStatsBar
                      stats={{
                        pending: pendingTodos,
                        overdue: overdueTodos,
                        inProgress: inProgressTodos,
                        onHold: onHoldTodos,
                        completed: completedTodos,
                      }}
                      onSelectStatus={(status) => setSelectedStatus(status)}
                      gapClass="me-3"
                    />
                  </div>
                </div>
              </div>

              <div className="row border-bottom mb-3">
                <div className="col-12">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                    <div className="d-flex align-items-center">
                      <h6 className="me-2 mb-0">Priority</h6>
                      <ul
                        className="nav nav-pills border d-inline-flex p-1 rounded bg-light todo-tabs"
                        id="pills-tab"
                        role="tablist"
                      >
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto ${
                              selectedPriority === "all" ? "active" : ""
                            }`}
                            onClick={() => handlePriorityChange("all")}
                            type="button"
                          >
                            All
                          </button>
                        </li>
                        {PRIORITY_OPTIONS.slice(1).map((option) => (
                          <li
                            className="nav-item"
                            role="presentation"
                            key={option.value}
                          >
                            <button
                              className={`nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto ${
                                selectedPriority === option.value ? "active" : ""
                              }`}
                              onClick={() => handlePriorityChange(option.value)}
                              type="button"
                            >
                              {option.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div className="d-flex align-items-center p-1 me-2">
                        <button
                          className="btn btn-outline-primary d-flex align-items-center"
                          onClick={toggleAllAccordions}
                        >
                          <i className="ti ti-arrows-maximize me-2" />
                          {accordionOpen.high &&
                          accordionOpen.medium &&
                          accordionOpen.low &&
                          accordionOpen.completed
                            ? "Collapse All"
                            : "Expand All"}
                        </button>
                      </div>
                    </div>

                    <div
                      className="input-icon-start position-relative flex-grow-1"
                      style={{ maxWidth: 200 }}
                    >
                      <span className="input-icon-addon">
                        <i className="ti ti-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-sm w-auto"
                        placeholder="Search Task List"
                        onChange={(e) => handleSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body p-0">
                <div className="tab-content" id="pills-tabContent">
                  <div
                    className="tab-pane fade show active"
                    id="pills-home"
                    role="tabpanel"
                  >
                    <div
                      className="accordion todo-accordion"
                      id="accordionExample"
                    >
                      {selectedPriority !== "all" &&
                        selectedPriority !== "completed" && (
                          <>
                            {renderAccordionSection(
                              "high",
                              filteredData.todos.filter(
                                (t) => t.priority === 0 && t.stage !== 3,
                              ),
                              "High",
                              "text-purple",
                            )}
                            {renderAccordionSection(
                              "medium",
                              filteredData.todos.filter(
                                (t) => t.priority === 1 && t.stage !== 3,
                              ),
                              "Medium",
                              "text-warning",
                            )}
                            {renderAccordionSection(
                              "low",
                              filteredData.todos.filter(
                                (t) => t.priority === 2 && t.stage !== 3,
                              ),
                              "Low",
                              "text-success",
                            )}
                          </>
                        )}

                      {selectedPriority === "all" && (
                        <>
                          {renderAccordionSection(
                            "high",
                            filteredData.todos.filter(
                              (t) => t.priority === 0 && t.stage !== 3,
                            ),
                            "High Priority",
                            "text-purple",
                          )}
                          {renderAccordionSection(
                            "medium",
                            filteredData.todos.filter(
                              (t) => t.priority === 1 && t.stage !== 3,
                            ),
                            "Medium Priority",
                            "text-warning",
                          )}
                          {renderAccordionSection(
                            "low",
                            filteredData.todos.filter(
                              (t) => t.priority === 2 && t.stage !== 3,
                            ),
                            "Low Priority",
                            "text-success",
                          )}
                        </>
                      )}

                      {renderAccordionSection(
                        "completed",
                        completedTodosSection,
                        "Completed",
                        "text-success",
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © ZYVO.</p>
          <p>
            Designed &amp; Developed By{" "}
            <a href="#" className="text-primary">
              ZYVO
            </a>
          </p>
        </div>
      </div>

      <TodoFilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        activeFiltersCount={activeFiltersCount}
        tempFilters={tempFilters}
        setTempFilters={setTempFilters}
        tagOptions={TAG_OPTIONS}
        statusOptions={STATUS_OPTIONS}
        recurringOptions={RECURRING_OPTIONS}
        reporterOptions={reporterImageOptions}
        followerOptions={assigneeImageOptions}
        assigneeOptions={assigneeImageOptions}
        onTagChange={handleTagFilterChange}
        onReporterChange={handleReporterFilterChange}
        onFollowerChange={handleFollowerFilterChange}
        onAssigneeChange={handleAssigneeFilterChange}
        onStatusChange={handleStatusFilterChange}
        onRecurringChange={handleRecurringFilterChange}
        onApply={applyFilters}
        onReset={resetFilters}
      />

      <TodoModal
        company={selectedCompany}
        todo={editModalTodo}
        viewTodo={viewModalTodo}
        onSave={handleModalSave}
      />

      <DeleteModal
        modalId="delete_modal"
        onDelete={() => handleDeleteTodo(deleteTodoId)}
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
      />

      <TransferTaskModal
        todo={transferModalTodo}
        signature={signature}
        onClose={() => setTransferModalTodo(null)}
        onTransferred={handleTaskTransferred}
      />

      <TaskHistoryModal
        todo={logsModalTodo}
        signature={signature}
        onClose={() => setLogsModalTodo(null)}
      />
    </>
  );
};

export default TodoGridView;
